import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPending, subscribePending } from "../utils/loadingTracker";
import { useSiteConfig } from "./SiteConfigContext";

const SETTLE_MS = 250; // wait this long with 0 pending requests before hiding
const MIN_SHOW_MS = 500; // avoid flicker
const MAX_SHOW_MS = 12000; // never block the user forever

// Static loader image (served from /public). Change this path to swap the image.
const LOADER_IMAGE = `${process.env.PUBLIC_URL || ""}/images/logo1.png`;

/**
 * Full-screen branded loader.
 * Shows on first load and on every route change, until all API requests settle.
 */
const PageLoader = () => {
  const { pathname } = useLocation();
  const { loading: configLoading } = useSiteConfig() || {};
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const shownAt = useRef(Date.now());
  const settleTimer = useRef(null);
  const maxTimer = useRef(null);
  const hideTimer = useRef(null);

  // Start loader on each route change
  useEffect(() => {
    clearTimeout(hideTimer.current);
    shownAt.current = Date.now();
    setFading(false);
    setVisible(true);

    clearTimeout(maxTimer.current);
    maxTimer.current = setTimeout(() => finish(true), MAX_SHOW_MS);

    scheduleCheck();
    return () => {
      clearTimeout(settleTimer.current);
      clearTimeout(maxTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Re-check whenever requests start/finish or site config finishes loading
  useEffect(() => {
    const unsub = subscribePending(scheduleCheck);
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  useEffect(() => {
    scheduleCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  function scheduleCheck() {
    clearTimeout(settleTimer.current);
    if (getPending() > 0 || configLoading) return; // still loading
    const wait = Math.max(
      SETTLE_MS,
      MIN_SHOW_MS - (Date.now() - shownAt.current),
    );
    settleTimer.current = setTimeout(() => {
      if (getPending() === 0) finish();
    }, wait);
  }

  function finish() {
    clearTimeout(maxTimer.current);
    setFading(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), 350);
  }

  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`page-loader${fading ? " page-loader--hide" : ""}`}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="page-loader__inner">
        <img className="page-loader__logo" src={LOADER_IMAGE} alt="Ivoréa" />
        <div className="page-loader__bar">
          <span />
        </div>
      </div>
    </div>
  );
};

export default PageLoader;
